/**
 * Guided setup (simple + advanced tiers). A thin shell over the pure builders
 * in wizard/apply.ts: collect answers, show the live headline numbers, hand a
 * finished Plan back. Couples answer inline (You / Spouse columns); every
 * question is skippable; "estimate for me" fills labeled defaults.
 */
import { useEffect, useMemo, useState } from 'react';
import type { DebtType, Plan, StateCode } from '../model/types';
import {
  applyAnswers, estimateSsMonthlyFromSalary, DEBT_LABELS,
  WINDFALL_DESTINATION_LABELS, type WindfallDestination, type WizardAnswers,
} from '../wizard/apply';
import { runProjection } from '../engine/projection';
import {
  saveLocalPlan, clearLocalPlan, saveWizardDraft, clearWizardDraft, type WizardDraft,
} from '../persist/local';
import { CurrencyField, NumberField, TextField } from './Inputs';
import { PersonRow } from './PersonRow';
import { netWorthToday } from '../model/netWorth';
import { TIPS } from './tips';
import { fmtCompactUsd, fmtUsd } from './format';

interface Props {
  tier: 'simple' | 'advanced';
  onFinish: (plan: Plan) => void;
  onExit: () => void;
  /** Keeps the app-level "remember" state in sync with the wizard's choice. */
  onRememberChange: (on: boolean) => void;
  /** Resume a remembered mid-setup session exactly where it stopped. */
  initialDraft?: WizardDraft;
}

const US_STATES = ['AK','AL','AR','AZ','CA','CO','CT','DC','DE','FL','GA','HI','IA','ID','IL','IN','KS','KY','LA','MA','MD','ME','MI','MN','MO','MS','MT','NC','ND','NE','NH','NJ','NM','NV','NY','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VA','VT','WA','WI','WV','WY'];

type GateKey =
  | 'debts' | 'laterIncome' | 'pensions' | 'rentals' | 'windfalls' | 'move' | 'ltc' | 'strategies';

/** Starting points for the stock share, named by how they feel rather than by
 *  jargon. The percentages are conventional glide-path anchors. */
const MIX_PRESETS = [
  { label: 'Conservative', equityPct: 40 },
  { label: 'Balanced', equityPct: 60 },
  { label: 'Growth', equityPct: 80 },
  { label: 'All stocks', equityPct: 100 },
];

const GATE_ORDER: GateKey[] = [
  'debts', 'laterIncome', 'pensions', 'rentals', 'windfalls', 'move', 'ltc', 'strategies',
];

const GATE_LABELS: Record<GateKey, { q: string; hint: string }> = {
  debts: { q: 'Any loans besides the mortgage?', hint: 'Car loans, student loans, credit cards, a home equity line — payments that end on a schedule rather than lasting forever.' },
  laterIncome: { q: 'Will any money still come in after you stop working?', hint: 'Consulting, part-time work, royalties, a board seat. Even a few years of it changes how early you can retire.' },
  pensions: { q: 'Does anyone have a pension or annuity coming?', hint: 'A monthly benefit from an old employer, the military, a government job, or an annuity you bought.' },
  rentals: { q: 'Do you own rental property?', hint: 'Property that pays you rent.' },
  windfalls: { q: 'Expecting a one-time windfall?', hint: 'An inheritance, a business or property sale, a settlement.' },
  move: { q: 'Planning to move to another state?', hint: 'State taxes differ enormously — moving is one of the biggest levers there is.' },
  ltc: { q: 'Model a long-term-care event?', hint: 'Roughly half of retirees need some paid care. Seeing what it does to the plan beats being surprised.' },
  strategies: { q: 'Interested in tax strategies?', hint: 'Roth conversions, a cash safety bucket, tax-loss harvesting — configured for you from a few questions.' },
};

/** The move step opens on this year — shared with GATE_SEEDS so what the screen
 *  shows and what gets modeled can never drift apart. There is deliberately NO
 *  default destination: state tax is the entire point of the question, and
 *  every state answers it differently, so the user has to pick one. */
const MOVE_DEFAULT_YEAR = 2036;

/**
 * Gates whose step opens on values that LOOK like an answer — a pre-filled age,
 * year or state rather than a blank zero. Ticking one of these seeds the answer,
 * so a user who agrees with everything on screen gets what the screen showed
 * them instead of a silent no-op.
 *
 * The other gates need no seed: debts, later income, pensions, rentals and
 * windfalls all open with ZERO amounts (a $0 row is visibly empty and models
 * nothing either way), and the strategies step opens with every box unchecked.
 */
const GATE_SEEDS: Partial<Record<GateKey, Partial<WizardAnswers>>> = {
  ltc: { ltc: { forYou: true } },
  move: { move: { year: MOVE_DEFAULT_YEAR } }, // destination stays unset on purpose
};


export function Wizard({ tier, onFinish, onExit, onRememberChange, initialDraft }: Props) {
  const [answers, setAnswers] = useState<WizardAnswers>(initialDraft?.answers ?? { you: {} });
  const [gates, setGates] = useState<Set<GateKey>>(
    new Set((initialDraft?.gates ?? []) as GateKey[]),
  );
  const [stepIndex, setStepIndex] = useState(initialDraft?.stepIndex ?? 0);
  // Asked up front, defaults OFF: a fresh wizard must never silently overwrite
  // a plan someone already saved on this device. A resumed draft only exists
  // because the user opted in, so it resumes with saving on.
  const [saveHere, setSaveHere] = useState(!!initialDraft);
  // One nudge when leaving step 1 unsaved — a reminder, not a nag.
  const [showSaveNudge, setShowSaveNudge] = useState(false);
  const [nudged, setNudged] = useState(false);

  const set = (patch: Partial<WizardAnswers>) => setAnswers((a) => ({ ...a, ...patch }));
  const setYou = (patch: Partial<WizardAnswers['you']>) =>
    setAnswers((a) => ({ ...a, you: { ...a.you, ...patch } }));
  const setSpouse = (patch: Partial<NonNullable<WizardAnswers['spouse']>>) =>
    setAnswers((a) => ({ ...a, spouse: { ...(a.spouse ?? {}), ...patch } }));

  // Step list adapts to answers (healthcare only pre-65, modules per gate).
  const steps = useMemo(() => {
    const retiresBefore65 =
      (answers.you.retirementAge ?? 65) < 65 ||
      (answers.hasSpouse && (answers.spouse?.retirementAge ?? 65) < 65);
    const list: string[] = ['about', 'income', 'savings', 'contributions', 'spending', 'home', 'social'];
    if (retiresBefore65) list.push('health');
    if (tier === 'advanced') {
      list.push('gates');
      for (const g of GATE_ORDER) if (gates.has(g)) list.push(g);
    }
    list.push('review');
    return list;
  }, [answers, gates, tier]);

  const step = steps[Math.min(stepIndex, steps.length - 1)]!;

  // Live headline preview: the whole point of answering is watching these move.
  const preview = useMemo(() => {
    const plan = applyAnswers(answers);
    const r = runProjection(plan, 2026);
    const last = r.snapshots[r.snapshots.length - 1]!;
    return {
      plan,
      now: netWorthToday(plan),
      ending: last.netWorth / last.cumulativeInflationFactor,
      depletion: r.firstDepletionYear,
      taxes: r.snapshots.reduce((t, s) => t + s.totalTaxLiability, 0),
    };
  }, [answers]);

  // While opted in, every answer keeps both on-device copies current: the
  // built plan (loadable outright) and the wizard draft (answers + step), so a
  // break mid-wizard resumes the wizard exactly where it stopped.
  useEffect(() => {
    if (saveHere) {
      saveLocalPlan(preview.plan, []);
      saveWizardDraft({ tier, answers, gates: [...gates], stepIndex });
    }
  }, [saveHere, preview.plan, tier, answers, gates, stepIndex]);

  const spouseOn = !!answers.hasSpouse;
  const rowProps = { spouseOn, youName: answers.you.name, spouseName: answers.spouse?.name };

  /**
   * A defensible first guess at annual spending: gross pay, less a rough tax
   * bite, less everything they already said they save. What is left is broadly
   * what passes through their hands each year.
   */
  const spendingAnchor = useMemo(() => {
    const people = [answers.you, ...(spouseOn && answers.spouse ? [answers.spouse] : [])];
    const gross = people.reduce((t, x) => t + (x.salary ?? 0), 0);
    if (gross <= 0) return 0;
    const saved = people.reduce(
      (t, x) => t + (x.contribution ?? 0) + (x.taxableSavingsPerYear ?? 0), 0,
    ) + (answers.taxableSavingsPerYear ?? 0);
    const effectiveTaxRate = gross > 250_000 ? 0.3 : gross > 120_000 ? 0.25 : 0.18;
    return Math.max(0, Math.round((gross * (1 - effectiveTaxRate) - saved) / 500) * 500);
  }, [answers, spouseOn]);

  const p = (who: 'you' | 'spouse') => (who === 'you' ? answers.you : answers.spouse ?? {});
  const setP = (who: 'you' | 'spouse') => (who === 'you' ? setYou : setSpouse);

  function body() {
    switch (step) {
      case 'about':
        return (
          <>
            <h2>Let's start with the basics</h2>
            <label className="wizard-gate">
              <input type="checkbox" checked={saveHere}
                onChange={(e) => {
                  const on = e.target.checked;
                  setSaveHere(on);
                  onRememberChange(on);
                  if (!on) { clearLocalPlan(); clearWizardDraft(); }
                }} />
              <span>
                <strong>Remember my progress on this device</strong>
                <span className="hint" style={{ display: 'block' }}>
                  Your answers save in this browser as you go — never sent anywhere — so you
                  can take a break and pick up from the front page. Left off, nothing is kept
                  unless you save at the end.
                </span>
              </span>
            </label>
            <div className="inputs-grid">
              <div className="field">
                <label>
                  Planning for
                  <select
                    value={spouseOn ? 'couple' : 'single'}
                    onChange={(e) => set({ hasSpouse: e.target.value === 'couple', spouse: e.target.value === 'couple' ? answers.spouse ?? {} : undefined })}
                  >
                    <option value="single">Just me</option>
                    <option value="couple">Me and my spouse/partner</option>
                  </select>
                </label>
              </div>
              <div className="field">
                <label>
                  State you live in
                  <select value={answers.state ?? 'TX'} onChange={(e) => set({ state: e.target.value as StateCode })}>
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <span className="hint">State tax is modeled per state — this matters more than people expect</span>
              </div>
            </div>
            <PersonRow {...rowProps}>
              {(who) => (
                <TextField label="Name (optional)" value={p(who).name ?? ''}
                  placeholder={who === 'you' ? 'You' : 'Spouse'}
                  hint={who === 'you' ? 'Just a label for the plan — a nickname is fine, and it never leaves your device' : undefined}
                  onCommit={(v) => setP(who)({ name: v || undefined })} />
              )}
            </PersonRow>
            <PersonRow {...rowProps}>
              {(who) => (
                <NumberField label="Age" min={18} max={90} value={p(who).age ?? (who === 'you' ? 50 : 48)}
                  onCommit={(v) => setP(who)({ age: v })} />
              )}
            </PersonRow>
            <PersonRow {...rowProps}>
              {(who) => (
                <NumberField label="Stop working at" min={35} max={80} tip={TIPS.retirementAge}
                  value={p(who).retirementAge ?? 65}
                  hint={who === 'you' ? 'Your best guess is fine — this is the single biggest lever in the plan' : undefined}
                  onCommit={(v) => setP(who)({ retirementAge: v })} />
              )}
            </PersonRow>
          </>
        );

      case 'income':
        return (
          <>
            <h2>What do you earn?</h2>
            <p className="hint">Gross annual pay, before taxes and deductions. Enter 0 if already retired.</p>
            <PersonRow {...rowProps}>
              {(who) => (
                <CurrencyField label="Annual salary" value={p(who).salary ?? 0} tip={TIPS.salary}
                  onCommit={(v) => setP(who)({ salary: v })} />
              )}
            </PersonRow>
          </>
        );

      case 'savings':
        return (
          <>
            <h2>What have you saved so far?</h2>
            <p className="hint">
              {spouseOn
                ? 'Enter each person\'s own accounts. Whose name is on an account decides when withdrawals are forced, when the early-withdrawal penalty lifts, and what the survivor keeps — so it is worth splitting rather than guessing. Jointly held cash can go under either of you.'
                : 'Rough totals are fine — you can refine account by account later.'}
            </p>
            <PersonRow {...rowProps}>
              {(w) => (
                <CurrencyField label="Pre-tax retirement (401k, 403b, IRA)"
                  value={p(w).pretax ?? 0} tip={TIPS.balance}
                  onCommit={(v) => setP(w)({ pretax: v })} />
              )}
            </PersonRow>
            <PersonRow {...rowProps}>
              {(w) => (
                <CurrencyField label="Roth accounts" value={p(w).roth ?? 0}
                  onCommit={(v) => setP(w)({ roth: v })} />
              )}
            </PersonRow>
            <PersonRow {...rowProps}>
              {(w) => (
                <CurrencyField label="Taxable brokerage" value={p(w).brokerage ?? 0}
                  hint={w === 'you' ? 'Regular investment account' : undefined}
                  onCommit={(v) => setP(w)({ brokerage: v })} />
              )}
            </PersonRow>
            {((answers.you.brokerage ?? 0) > 0 || (answers.spouse?.brokerage ?? 0) > 0) && (
              <PersonRow {...rowProps}>
                {(w) => (
                  <CurrencyField label="…of which you originally paid in (cost basis)"
                    value={p(w).brokerageBasis ?? 0} tip={TIPS.costBasis}
                    hint={w === 'you' ? 'Only the growth above this is taxed when sold. Leave blank and we estimate 70%.' : undefined}
                    onCommit={(v) => setP(w)({ brokerageBasis: v || undefined })} />
                )}
              </PersonRow>
            )}
            <PersonRow {...rowProps}>
              {(w) => (
                <CurrencyField label="Cash & savings" value={p(w).cash ?? 0}
                  onCommit={(v) => setP(w)({ cash: v })} />
              )}
            </PersonRow>
            <PersonRow {...rowProps}>
              {(w) => (
                <CurrencyField label="T-bills, treasuries & bonds" value={p(w).bonds ?? 0}
                  hint={w === 'you' ? 'Held for safety rather than growth. If you keep a cash cushion for bad markets, put it here — the tax-strategy step can use it as your safety bucket.' : undefined}
                  onCommit={(v) => setP(w)({ bonds: v })} />
              )}
            </PersonRow>
            <PersonRow {...rowProps}>
              {(w) => (
                <CurrencyField label="HSA (if any)" value={p(w).hsa ?? 0}
                  onCommit={(v) => setP(w)({ hsa: v })} />
              )}
            </PersonRow>

            <div className="wizard-subhead">How is it invested?</div>
            <p className="hint">
              Stocks grow fastest over decades and fall hardest in bad years; bonds and cash earn
              less but steady the ride. This sets the return your balances earn — pick the closest
              and fine-tune per account later.
            </p>
            <div className="seg" role="group" aria-label="Investment mix" style={{ marginBottom: 'var(--space-3)' }}>
              {MIX_PRESETS.map((m) => (
                <button key={m.label} aria-pressed={(answers.equityPct ?? 60) === m.equityPct}
                  onClick={() => set({ equityPct: m.equityPct })}>
                  {m.label}
                </button>
              ))}
            </div>
            <div className="inputs-grid">
              <NumberField label="Stocks %" min={0} max={100} value={answers.equityPct ?? 60}
                tip={TIPS.equityPct}
                hint={`The other ${100 - (answers.equityPct ?? 60)}% goes to bonds and cash.`}
                onCommit={(v) => set({ equityPct: v })} />
            </div>
          </>
        );

      case 'contributions':
        return (
          <>
            <h2>What are you still adding?</h2>
            <PersonRow {...rowProps}>
              {(who) => (
                <CurrencyField label="401(k)/IRA per year" value={p(who).contribution ?? 0}
                  hint={who === 'you' ? '2026 401(k) limit: $24,500 (+$8,000 at 50)' : undefined}
                  onCommit={(v) => setP(who)({ contribution: v })} />
              )}
            </PersonRow>
            <PersonRow {...rowProps}>
              {(who) => (
                <NumberField label="Employer matches first ...% of pay" min={0} max={25}
                  value={p(who).matchPercentOfSalary ?? 0}
                  hint={who === 'you' ? '0 if no match. "50% up to 6%" means: enter 6 here, 50 below' : undefined}
                  onCommit={(v) => setP(who)({ matchPercentOfSalary: v || undefined })} />
              )}
            </PersonRow>
            <PersonRow {...rowProps}>
              {(who) => (
                <NumberField label="...at a match rate of %" min={0} max={200}
                  value={Math.round((p(who).matchRate ?? 0) * 100)}
                  onCommit={(v) => setP(who)({ matchRate: v ? v / 100 : undefined })} />
              )}
            </PersonRow>
            <PersonRow {...rowProps}>
              {(who) => (
                <CurrencyField label="Into stocks per year (brokerage)"
                  value={p(who).taxableSavingsPerYear ?? answers.taxableSavingsPerYear ?? 0}
                  hint={who === 'you' ? 'Beyond retirement accounts — no federal limit' : undefined}
                  tip={TIPS.taxableSavings}
                  onCommit={(v) => setP(who)({ taxableSavingsPerYear: v })} />
              )}
            </PersonRow>
            <PersonRow {...rowProps}>
              {(who) => (
                <CurrencyField label="Into T-bills / bonds per year"
                  value={p(who).bondSavingsPerYear ?? 0}
                  hint={who === 'you' ? 'The usual way a safety bucket gets built up before retirement' : undefined}
                  onCommit={(v) => setP(who)({ bondSavingsPerYear: v })} />
              )}
            </PersonRow>
          </>
        );

      case 'spending':
        return (
          <>
            <h2>What does your life cost?</h2>
            {spendingAnchor > 0 && (
              <div className="wizard-suggest">
                <strong>Not sure? Start from what you earn.</strong>
                <p className="hint">
                  Your pay, less an estimate for taxes and everything you already save, leaves
                  about <strong>{fmtUsd(spendingAnchor)}</strong> a year. That is roughly what
                  passes through your hands today, so it is the best first guess at what your life
                  costs. Pick the one that sounds like you — you can type over it.
                </p>
                <div className="add-row">
                  {[
                    { label: 'I live below my means', factor: 0.8 },
                    { label: 'That sounds about right', factor: 1 },
                    { label: 'I spend most of what I make', factor: 1.15 },
                  ].map((o) => {
                    const value = Math.round((spendingAnchor * o.factor) / 500) * 500;
                    return (
                      <button key={o.label} className="btn"
                        aria-pressed={answers.annualSpending === value}
                        onClick={() => set({ annualSpending: value })}>
                        {o.label} · {fmtUsd(value)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="inputs-grid">
              <CurrencyField label="Annual spending today" value={answers.annualSpending ?? 0}
                hint="Everything except loan payments — those come next" tip={TIPS.annualSpending}
                onCommit={(v) => set({ annualSpending: v })} />
              <NumberField label="In retirement, spend ...% of that" min={40} max={150}
                value={answers.retirementSpendingPct ?? 90}
                hint="Most retirees land around 80-90%" tip={TIPS.retirementSpendingPct}
                onCommit={(v) => set({ retirementSpendingPct: v })} />
              <NumberField label="Roughly ...% of spending is essential" min={10} max={100}
                value={answers.essentialPct ?? 65}
                hint="Housing, food, insurance — the part you can't cut" tip={TIPS.essentialPct}
                onCommit={(v) => set({ essentialPct: v })} />
            </div>
          </>
        );

      case 'home':
        return (
          <>
            <h2>Your home</h2>
            <div className="inputs-grid">
              <div className="field">
                <label>
                  Do you own your home?
                  <select value={answers.ownsHome ? 'yes' : 'no'}
                    onChange={(e) => set({ ownsHome: e.target.value === 'yes' })}>
                    <option value="no">No — renting (rent goes in spending)</option>
                    <option value="yes">Yes</option>
                  </select>
                </label>
              </div>
            </div>
            {answers.ownsHome && (
              <div className="inputs-grid" style={{ marginTop: 'var(--space-3)' }}>
                <CurrencyField label="What it's worth" value={answers.homeValue ?? 0} tip={TIPS.homeValue}
                  onCommit={(v) => set({ homeValue: v })} />
                <CurrencyField label="What you paid for it (or spent building it)"
                  value={answers.homeCostBasis ?? 0} tip={TIPS.homeBasis}
                  hint="Plus major improvements. Only matters if the plan sells the home; leave blank and we'll estimate."
                  onCommit={(v) => set({ homeCostBasis: v || undefined })} />
                <CurrencyField label="Property tax per year" value={answers.propertyTaxAnnual ?? 0}
                  tip={TIPS.propertyTax}
                  onCommit={(v) => set({ propertyTaxAnnual: v })} />
                <CurrencyField label="Mortgage balance (0 if paid off)" value={answers.mortgageBalance ?? 0}
                  tip={TIPS.mortgageBalance}
                  onCommit={(v) => set({ mortgageBalance: v })} />
                {(answers.mortgageBalance ?? 0) > 0 && (
                  <>
                    <NumberField label="Mortgage rate %" min={0} max={12}
                      value={Math.round((answers.mortgageRate ?? 0.06) * 100)}
                      onCommit={(v) => set({ mortgageRate: v / 100 })} />
                    <CurrencyField label="Monthly payment" value={answers.mortgagePayment ?? 0}
                      onCommit={(v) => set({ mortgagePayment: v })} />
                  </>
                )}
              </div>
            )}
          </>
        );

      case 'social':
        return (
          <>
            <h2>Social Security</h2>
            <p className="hint">
              The exact number is on your statement at ssa.gov ("benefit at full retirement
              age"). No statement handy? Let the planner estimate it from your salary — clearly
              labeled as an estimate.
            </p>
            <PersonRow {...rowProps}>
              {(who) => (
                <div>
                  <CurrencyField label="Monthly benefit at full retirement age"
                    value={p(who).ssMonthlyAtFRA ?? 0} tip={TIPS.ssMonthly}
                    onCommit={(v) => setP(who)({ ssMonthlyAtFRA: v || undefined, ssEstimated: false })} />
                  {!p(who).ssMonthlyAtFRA && (p(who).salary ?? 0) > 0 && (
                    <button className="btn" style={{ marginTop: 6 }}
                      onClick={() => setP(who)({ ssEstimated: true, ssMonthlyAtFRA: undefined })}>
                      {p(who).ssEstimated
                        ? `Estimated: $${estimateSsMonthlyFromSalary(p(who).salary!).toLocaleString()}/mo ✓`
                        : 'Estimate it from my salary'}
                    </button>
                  )}
                </div>
              )}
            </PersonRow>
            <PersonRow {...rowProps}>
              {(who) => (
                <NumberField label="Claim at age" min={62} max={70} value={p(who).claimingAge ?? 67}
                  tip={TIPS.claimingAge}
                  hint={who === 'you' ? '62 = reduced · 67 = full · 70 = maximum. The claiming explorer can optimize this later.' : undefined}
                  onCommit={(v) => setP(who)({ claimingAge: v })} />
              )}
            </PersonRow>
          </>
        );

      case 'health':
        return (
          <>
            <h2>Health insurance before Medicare</h2>
            <p className="hint">
              Someone here retires before 65, so there are years to bridge before Medicare —
              usually the largest expense of early retirement.
            </p>
            <div className="inputs-grid">
              <div className="field">
                <label>
                  How will you be covered?
                  <select value={answers.preMedicare ?? 'aca'}
                    onChange={(e) => set({ preMedicare: e.target.value as 'aca' | 'override' })}>
                    <option value="aca">Marketplace (ACA) — model the subsidy for me</option>
                    <option value="override">I know my cost (retiree plan, COBRA, spouse's plan)</option>
                  </select>
                </label>
              </div>
              <CurrencyField
                label={answers.preMedicare === 'override' ? 'My cost, $/month per person' : 'Full-price premium, $/month per person'}
                value={answers.preMedicareMonthly ?? (answers.preMedicare === 'override' ? 0 : 1_350)}
                hint={answers.preMedicare === 'override' ? undefined : '~$1,350/mo at 60-64 nationally; the subsidy comes off this based on your income'}
                onCommit={(v) => set({ preMedicareMonthly: v })} />
            </div>
          </>
        );

      case 'gates':
        return (
          <>
            <h2>What else applies to you?</h2>
            <p className="hint">Check what's relevant — the wizard only asks about what you check.</p>
            {GATE_ORDER.map((g) => (
              <label key={g} className="wizard-gate">
                <input type="checkbox" checked={gates.has(g)}
                  onChange={(e) => {
                    const next = new Set(gates);
                    if (e.target.checked) next.add(g);
                    else next.delete(g);
                    setGates(next);
                    const seed = GATE_SEEDS[g];
                    if (seed && e.target.checked && !(g in answers)) set(seed);
                  }} />
                <span>
                  <strong>{GATE_LABELS[g].q}</strong>
                  <span className="hint" style={{ display: 'block' }}>{GATE_LABELS[g].hint}</span>
                </span>
              </label>
            ))}
          </>
        );

      case 'debts': {
        const blank = { kind: 'auto' as DebtType, balance: 0, annualRate: 0.07, monthlyPayment: 0 };
        const rows = answers.debts?.length ? answers.debts : [blank];
        const upd = (i: number, patch: Partial<(typeof rows)[number]>) =>
          set({ debts: rows.map((d, j) => (j === i ? { ...d, ...patch } : d)) });
        return (
          <>
            <h2>Other loans</h2>
            <p className="hint">
              These end on a schedule, which frees up cash flow later — the plan shows exactly when.
            </p>
            {rows.map((d, i) => (
              <div key={`${rows.length}-${i}`} className="wizard-multi-row">
                {rows.length > 1 && (
                  <button className="btn wizard-multi-remove"
                    onClick={() => set({ debts: rows.filter((_, j) => j !== i) })}>
                    Remove
                  </button>
                )}
                <div className="inputs-grid">
                  <div className="field">
                    <label>
                      What kind?
                      <select value={d.kind}
                        onChange={(e) => upd(i, { kind: e.target.value as DebtType })}>
                        {(['auto', 'student', 'credit_card', 'personal', 'heloc'] as DebtType[]).map((k) => (
                          <option key={k} value={k}>{DEBT_LABELS[k]}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <CurrencyField label="Balance owed" value={d.balance}
                    onCommit={(v) => upd(i, { balance: v })} />
                  <NumberField label="Interest rate %" min={0} max={40}
                    value={Math.round((d.annualRate ?? 0) * 100)}
                    onCommit={(v) => upd(i, { annualRate: v / 100 })} />
                  <CurrencyField label="Monthly payment" value={d.monthlyPayment}
                    onCommit={(v) => upd(i, { monthlyPayment: v })} />
                </div>
              </div>
            ))}
            <button className="btn" onClick={() => set({ debts: [...rows, blank] })}>
              + Add another loan
            </button>
          </>
        );
      }

      case 'laterIncome': {
        const defaultStart = answers.you.retirementAge ?? 65;
        const blank = {
          owner: 'you' as const, label: 'Consulting', annualAmount: 0,
          startAge: defaultStart, endAge: defaultStart + 5,
        };
        const rows = answers.retirementIncome?.length ? answers.retirementIncome : [blank];
        const upd = (i: number, patch: Partial<(typeof rows)[number]>) =>
          set({ retirementIncome: rows.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
        return (
          <>
            <h2>Income after you stop working</h2>
            <p className="hint">
              Taxed as ordinary income, and it counts toward the ACA subsidy calculation in
              pre-Medicare years.
            </p>
            {rows.map((inc, i) => (
              <div key={`${rows.length}-${i}`} className="wizard-multi-row">
                {rows.length > 1 && (
                  <button className="btn wizard-multi-remove"
                    onClick={() => set({ retirementIncome: rows.filter((_, j) => j !== i) })}>
                    Remove
                  </button>
                )}
                <div className="inputs-grid">
                  {spouseOn && (
                    <div className="field">
                      <label>
                        Whose is it?
                        <select value={inc.owner}
                          onChange={(e) => upd(i, { owner: e.target.value as 'you' | 'spouse' })}>
                          <option value="you">{answers.you.name || 'Mine'}</option>
                          <option value="spouse">{answers.spouse?.name || 'My spouse\'s'}</option>
                        </select>
                      </label>
                    </div>
                  )}
                  <TextField label="What is it?" value={inc.label}
                    placeholder="Consulting"
                    onCommit={(v) => upd(i, { label: v })} />
                  <CurrencyField label="Amount per year" value={inc.annualAmount}
                    onCommit={(v) => upd(i, { annualAmount: v })} />
                  <NumberField label="From age" min={40} max={90} value={inc.startAge}
                    onCommit={(v) => upd(i, { startAge: v })} />
                  <NumberField label="Until age" min={40} max={100} value={inc.endAge}
                    onCommit={(v) => upd(i, { endAge: v })} />
                </div>
              </div>
            ))}
            <button className="btn" onClick={() => set({ retirementIncome: [...rows, blank] })}>
              + Add another
            </button>
          </>
        );
      }

      case 'pensions': {
        const rows = answers.pensions?.length
          ? answers.pensions
          : [{ owner: 'you' as const, annualAmount: 0, startAge: 65 }];
        const upd = (i: number, patch: Partial<(typeof rows)[number]>) =>
          set({ pensions: rows.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
        return (
          <>
            <h2>Pension / annuity</h2>
            {rows.map((pen, i) => (
              <div key={`${rows.length}-${i}`} className="wizard-multi-row">
                {rows.length > 1 && (
                  <button className="btn wizard-multi-remove"
                    onClick={() => set({ pensions: rows.filter((_, j) => j !== i) })}>
                    Remove
                  </button>
                )}
                <div className="inputs-grid">
                  {spouseOn && (
                    <div className="field">
                      <label>
                        Whose is it?
                        <select value={pen.owner}
                          onChange={(e) => upd(i, { owner: e.target.value as 'you' | 'spouse' })}>
                          <option value="you">Mine</option>
                          <option value="spouse">My spouse's</option>
                        </select>
                      </label>
                    </div>
                  )}
                  <CurrencyField label="Amount per year" value={pen.annualAmount}
                    hint="What the statement shows for the survivor option you chose"
                    onCommit={(v) => upd(i, { annualAmount: v })} />
                  <NumberField label="Starts at age" min={50} max={75} value={pen.startAge}
                    onCommit={(v) => upd(i, { startAge: v })} />
                  {spouseOn && (
                    <NumberField label="Survivor % (0 = single-life)" min={0} max={100}
                      value={pen.survivorPercent ?? 0}
                      hint="What the surviving spouse keeps receiving"
                      onCommit={(v) => upd(i, { survivorPercent: v || undefined })} />
                  )}
                </div>
              </div>
            ))}
            <button className="btn"
              onClick={() => set({ pensions: [...rows, { owner: 'you', annualAmount: 0, startAge: 65 }] })}>
              + Add another pension
            </button>
          </>
        );
      }

      case 'rentals': {
        const blank: NonNullable<WizardAnswers['rentals']>[number] = {
          value: 0, costBasis: 0, grossRent: 0, vacancyPct: 0.08, expensesAnnual: 0,
        };
        const rows = answers.rentals?.length ? answers.rentals : [blank];
        const upd = (i: number, patch: Partial<(typeof rows)[number]>) =>
          set({ rentals: rows.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
        return (
          <>
            <h2>Rental property</h2>
            {rows.map((r, i) => (
              <div key={`${rows.length}-${i}`} className="wizard-multi-row">
                {rows.length > 1 && (
                  <button className="btn wizard-multi-remove"
                    onClick={() => set({ rentals: rows.filter((_, j) => j !== i) })}>
                    Remove
                  </button>
                )}
                <div className="inputs-grid">
                  <CurrencyField label="Property value" value={r.value}
                    onCommit={(v) => upd(i, { value: v })} />
                  <CurrencyField label="What you paid (basis)" value={r.costBasis}
                    onCommit={(v) => upd(i, { costBasis: v })} />
                  <CurrencyField label="Gross rent per year" value={r.grossRent}
                    onCommit={(v) => upd(i, { grossRent: v })} />
                  <CurrencyField label="Operating costs per year" value={r.expensesAnnual}
                    hint="Repairs and management — taxes and insurance go below"
                    onCommit={(v) => upd(i, { expensesAnnual: v })} />
                  <CurrencyField label="Property tax per year" value={r.propertyTaxAnnual ?? 0}
                    onCommit={(v) => upd(i, { propertyTaxAnnual: v || undefined })} />
                  <CurrencyField label="Insurance per year" value={r.insuranceAnnual ?? 0}
                    onCommit={(v) => upd(i, { insuranceAnnual: v || undefined })} />
                </div>
              </div>
            ))}
            <button className="btn" onClick={() => set({ rentals: [...rows, blank] })}>
              + Add another rental
            </button>
          </>
        );
      }

      case 'windfalls': {
        const blank: NonNullable<WizardAnswers['windfalls']>[number] = {
          year: 2030, amount: 0, kind: 'inheritance', destination: 'spend',
        };
        const rows = answers.windfalls?.length ? answers.windfalls : [blank];
        const upd = (i: number, patch: Partial<(typeof rows)[number]>) =>
          set({ windfalls: rows.map((w, j) => (j === i ? { ...w, ...patch } : w)) });
        return (
          <>
            <h2>One-time windfalls</h2>
            <p className="hint">
              Inheritances, sales, bonuses, settlements — add as many as you expect.
            </p>
            {rows.map((w, i) => (
              <div key={`${rows.length}-${i}`} className="wizard-multi-row">
                {rows.length > 1 && (
                  <button className="btn wizard-multi-remove"
                    onClick={() => set({ windfalls: rows.filter((_, j) => j !== i) })}>
                    Remove
                  </button>
                )}
                <div className="inputs-grid">
                  <div className="field">
                    <label>
                      What kind?
                      <select value={w.kind}
                        onChange={(e) => upd(i, { kind: e.target.value as 'inheritance' | 'sale' | 'bonus' })}>
                        <option value="inheritance">Inheritance / gift (tax-free)</option>
                        <option value="sale">Sale of a business or property (long-term capital gain)</option>
                        <option value="bonus">Work bonus or payout (taxed like a paycheck)</option>
                      </select>
                    </label>
                    {w.kind === 'bonus' && (
                      <span className="hint">
                        Ordinary income — your top bracket plus state tax, and it counts toward ACA
                        subsidies and IRMAA that year.
                      </span>
                    )}
                  </div>
                  <NumberField label="Around what year?" min={2026} max={2070} value={w.year}
                    onCommit={(v) => upd(i, { year: v })} />
                  <CurrencyField label="Roughly how much" value={w.amount}
                    onCommit={(v) => upd(i, { amount: v })} />
                  {w.kind === 'sale' && (
                    <CurrencyField label="What you paid for it (cost basis)" value={w.saleBasis ?? 0}
                      tip={TIPS.eventCostBasis}
                      hint="Only the gain above this is taxed. Leave at 0 to tax the whole amount — typical for a business you built."
                      onCommit={(v) => upd(i, { saleBasis: v || undefined })} />
                  )}
                </div>

                <div className="wizard-subhead">Where does it go?</div>
                {w.kind !== 'inheritance' && w.amount > 0 && (
                  <p className="hint">
                    Enter gross amounts. The planner holds back an estimate of the tax first, so
                    roughly {fmtUsd(Math.round(w.amount * (w.kind === 'bonus' ? 0.72 : 0.82)))} of
                    this actually reaches your accounts.
                  </p>
                )}
                <div className="inputs-grid">
                  <div className="field">
                    <label>
                      Send some of it first to
                      <select value={w.splitDestination ?? ''}
                        onChange={(e) => upd(i, {
                          splitDestination: (e.target.value || undefined) as WindfallDestination | undefined,
                        })}>
                        <option value="">No split — it all goes to one place</option>
                        {(['bucket', 'cash', 'bonds', 'brokerage'] as WindfallDestination[]).map((d) => (
                          <option key={d} value={d}>{WINDFALL_DESTINATION_LABELS[d]}</option>
                        ))}
                      </select>
                    </label>
                    <span className="hint">Handy for filling a safety bucket before the rest is invested.</span>
                  </div>
                  {w.splitDestination && (
                    <CurrencyField label="How much goes there" value={w.splitAmount ?? 0}
                      onCommit={(v) => upd(i, { splitAmount: v || undefined })} />
                  )}
                  <div className="field">
                    <label>
                      {w.splitDestination ? 'The rest goes to' : 'It all goes to'}
                      <select value={w.destination ?? 'spend'}
                        onChange={(e) => upd(i, { destination: e.target.value as WindfallDestination })}>
                        {(['spend', 'brokerage', 'cash', 'bonds', 'bucket'] as WindfallDestination[]).map((d) => (
                          <option key={d} value={d}>{WINDFALL_DESTINATION_LABELS[d]}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>
              </div>
            ))}
            <button className="btn" onClick={() => set({ windfalls: [...rows, blank] })}>
              + Add another windfall
            </button>
          </>
        );
      }

      case 'move': {
        const m = answers.move;
        return (
          <>
            <h2>Moving states</h2>
            <div className="inputs-grid">
              <NumberField label="Around what year?" min={2026} max={2070}
                value={m?.year ?? MOVE_DEFAULT_YEAR}
                onCommit={(v) => set({ move: { ...(m ?? {}), year: v } })} />
              <div className="field">
                <label>
                  To which state?
                  <select value={m?.toState ?? ''}
                    onChange={(e) => set({
                      move: {
                        year: MOVE_DEFAULT_YEAR,
                        ...(m ?? {}),
                        toState: (e.target.value || undefined) as StateCode | undefined,
                      },
                    })}>
                    <option value="">Pick a state…</option>
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <span className="hint">
                  {m?.toState
                    ? 'The planner will show the lifetime tax difference vs. staying'
                    : 'No default here on purpose — the answer swings entirely on which state, so pick yours'}
                </span>
              </div>
            </div>
          </>
        );
      }

      case 'ltc': {
        // Which option the dropdown is showing. "Both" needs its own branch:
        // testing forSpouse first would render "My spouse" whenever both are
        // set, snapping the control back and looking like the choice refused
        // to stick. Guarded by spouseOn so removing a spouse can't leave the
        // select pointing at an option that is no longer rendered.
        const ltcWho = spouseOn && answers.ltc?.forSpouse
          ? (answers.ltc?.forYou ? 'both' : 'spouse')
          : 'you';
        return (
          <>
            <h2>Long-term care</h2>
            <div className="inputs-grid">
              <div className="field">
                <label>
                  Model a care event for...
                  <select
                    value={ltcWho}
                    onChange={(e) => set({
                      ltc: {
                        ...(answers.ltc ?? {}),
                        forYou: e.target.value !== 'spouse',
                        forSpouse: spouseOn && e.target.value !== 'you',
                      },
                    })}>
                    <option value="you">Me</option>
                    {spouseOn && <option value="spouse">My spouse</option>}
                    {spouseOn && <option value="both">Both of us</option>}
                  </select>
                </label>
              </div>
              <NumberField label="Care starts around age" min={65} max={100}
                value={answers.ltc?.onsetAge ?? 85}
                onCommit={(v) => set({ ltc: { forYou: true, ...(answers.ltc ?? {}), onsetAge: v } })} />
              <CurrencyField label="Annual cost (today's $)" value={answers.ltc?.annualCostToday ?? 110_000}
                hint="Assisted living ~$70-110k · nursing home $120k+"
                onCommit={(v) => set({ ltc: { forYou: true, ...(answers.ltc ?? {}), annualCostToday: v } })} />
            </div>
          </>
        );
      }

      case 'strategies': {
        const s = answers.strategies ?? {};
        return (
          <>
            <h2>Tax strategies</h2>
            <p className="hint">Each of these gets configured now and is adjustable later under Strategy.</p>

            <label className="wizard-gate">
              <input type="checkbox" checked={!!s.rothConversions}
                onChange={(e) => set({ strategies: { ...s, rothConversions: e.target.checked ? { bracket: 0.22, startAtMedicare: true } : undefined } })} />
              <span><strong>Roth conversions</strong>
                <span className="hint" style={{ display: 'block' }}>Move pre-tax money to Roth in low-tax years, defusing later RMDs.</span></span>
            </label>
            {s.rothConversions && (
              <div className="inputs-grid" style={{ margin: '0 0 var(--space-3) 28px' }}>
                <div className="field">
                  <label>
                    Fill income to the top of
                    <select value={String(s.rothConversions.bracket)}
                      onChange={(e) => set({ strategies: { ...s, rothConversions: { ...s.rothConversions!, bracket: Number(e.target.value) } } })}>
                      <option value="0.12">the 12% bracket (gentle)</option>
                      <option value="0.22">the 22% bracket (typical)</option>
                      <option value="0.24">the 24% bracket (aggressive)</option>
                    </select>
                  </label>
                </div>
                <div className="field">
                  <label>
                    Start when?
                    <select value={s.rothConversions.startAtMedicare ? 'medicare' : 'retirement'}
                      onChange={(e) => set({ strategies: { ...s, rothConversions: { ...s.rothConversions!, startAtMedicare: e.target.value === 'medicare' } } })}>
                      <option value="medicare">At 65 — protects any ACA subsidy first</option>
                      <option value="retirement">At retirement — more years, but raises pre-65 income</option>
                    </select>
                  </label>
                </div>
              </div>
            )}

            <label className="wizard-gate">
              <input type="checkbox" checked={!!s.bucket}
                onChange={(e) => set({ strategies: { ...s, bucket: e.target.checked ? { targetToday: 250_000, protectAca: true } : undefined } })} />
              <span><strong>Cash safety bucket</strong>
                <span className="hint" style={{ display: 'block' }}>A T-bill/money-market cushion spent in down markets (and low-income bridge years) instead of selling investments.</span></span>
            </label>
            {s.bucket && (() => {
              const bucket = s.bucket!;
              const upd = (patch: Partial<typeof bucket>) =>
                set({ strategies: { ...s, bucket: { ...bucket, ...patch } } });
              const bondsToday = (answers.you.bonds ?? 0) + (answers.spouse?.bonds ?? 0);
              const bondSaving =
                (answers.you.bondSavingsPerYear ?? 0) + (answers.spouse?.bondSavingsPerYear ?? 0);
              const fundableWindfalls = (answers.windfalls ?? [])
                .map((w, i) => ({ w, i }))
                .filter((x) => x.w.amount > 0);
              return (
                <div style={{ margin: '0 0 var(--space-3) 28px' }}>
                  <div className="inputs-grid">
                    <CurrencyField label="Bucket size (today's $)" value={bucket.targetToday}
                      hint="2-4 years of spending is the common range"
                      tip={TIPS.safetyBucket}
                      onCommit={(v) => upd({ targetToday: v })} />
                    <div className="field">
                      <label>
                        How does it get filled?
                        <select value={bucket.fundedBy ?? 'existing'}
                          onChange={(e) => upd({ fundedBy: e.target.value as 'existing' | 'event' | 'savings' })}>
                          <option value="existing">
                            {bondsToday > 0
                              ? `I already hold it — ${fmtUsd(bondsToday)} in T-bills/bonds`
                              : 'I already hold it'}
                          </option>
                          {fundableWindfalls.length > 0 && (
                            <option value="event">From a one-time event</option>
                          )}
                          <option value="savings">
                            {bondSaving > 0
                              ? `Building it up — ${fmtUsd(bondSaving)}/yr into bonds`
                              : 'Building it up from savings'}
                          </option>
                        </select>
                      </label>
                    </div>
                    {bucket.fundedBy === 'event' && fundableWindfalls.length > 0 && (
                      <div className="field">
                        <label>
                          Which event?
                          <select value={String(bucket.fundFromWindfallIndex ?? fundableWindfalls[0]!.i)}
                            onChange={(e) => upd({ fundFromWindfallIndex: Number(e.target.value) })}>
                            {fundableWindfalls.map(({ w, i }) => (
                              <option key={i} value={i}>
                                {w.year} · {fmtUsd(w.amount)} ·{' '}
                                {w.kind === 'sale' ? 'sale' : w.kind === 'bonus' ? 'bonus' : 'inheritance'}
                              </option>
                            ))}
                          </select>
                        </label>
                        <span className="hint">
                          Taxes are set aside first, then up to {fmtUsd(bucket.targetToday)} fills
                          the bucket and the rest follows that event's own instructions. If what
                          survives the tax bill is smaller, the bucket simply gets what there is.
                        </span>
                      </div>
                    )}
                  </div>
                  {bucket.fundedBy === 'existing' && bondsToday === 0 && (
                    <p className="hint">
                      No T-bills or bonds were entered earlier, so the bucket starts empty and the
                      plan will simply behave as if it has one. Go back a few steps to enter what
                      you hold, or pick another way to fill it.
                    </p>
                  )}
                </div>
              );
            })()}

            <label className="wizard-gate">
              <input type="checkbox" checked={!!s.tlh}
                onChange={(e) => set({ strategies: { ...s, tlh: e.target.checked ? { baselinePct: 0.01 } : undefined } })} />
              <span><strong>Tax-loss harvesting</strong>
                <span className="hint" style={{ display: 'block' }}>Bank losses in the brokerage to offset gains — most valuable in low-income years before 65.</span></span>
            </label>
          </>
        );
      }

      case 'review':
        return (
          <>
            <h2>Your plan is ready</h2>
            <p className="hint">
              Everything below is editable in the planner — the wizard just gave you a running
              start. Two things worth doing next: check the Social Security heat map, and save
              your plan (nothing is stored unless you choose to).
            </p>
            <div className="hero-row" style={{ margin: 'var(--space-4) 0' }}>
              <div className="stat">
                <div className="label">Net worth today</div>
                <div className="value">{fmtCompactUsd(preview.now)}</div>
              </div>
              <div className="stat">
                <div className="label">{preview.depletion ? 'Money runs out' : 'At the end'}</div>
                <div className={`value${preview.depletion ? ' negative' : ''}`}>
                  {preview.depletion ? preview.depletion : fmtCompactUsd(preview.ending)}
                </div>
              </div>
              <div className="stat">
                <div className="label">Lifetime taxes</div>
                <div className="value">{fmtCompactUsd(preview.taxes)}</div>
              </div>
            </div>
            {answers.you.ssEstimated && (
              <p className="hint">
                ⚠ Social Security was estimated from salary — the real number is on ssa.gov and
                is worth the two minutes.
              </p>
            )}
          </>
        );

      default:
        return null;
    }
  }

  const atStart = stepIndex === 0;
  const atReview = step === 'review';

  /**
   * Why this step can't be left yet, or null. Kept deliberately narrow: the
   * wizard's whole posture is that you can skip and refine later. The one thing
   * worth stopping for is an answer that would otherwise be dropped in silence —
   * a move with no destination models nothing, and the user would never know.
   */
  const blocker: string | null =
    step === 'move' && !answers.move?.toState
      ? 'Pick the state you would move to — a move without one changes nothing in the plan. If you are not sure yet, go back and untick "planning to move".'
      : null;

  /** The ONLY way forward. Every path — the Next button and both save-nudge
   *  buttons — goes through here, so a blocked step cannot be stepped over by
   *  whichever control happens to be on screen. */
  const goNext = () => {
    if (blocker) return;
    setStepIndex((i) => Math.min(steps.length - 1, i + 1));
  };

  return (
    <div className="app wizard">
      <div className="wizard-head">
        <span className="wizard-progress">
          {tier === 'simple' ? 'Quick setup' : 'Full setup'} · step {stepIndex + 1} of {steps.length}
          {saveHere && <span className="wizard-saved"> · ✓ progress saved on this device</span>}
        </span>
        <button className="btn" onClick={onExit}>Exit setup</button>
      </div>

      {!atReview && (
        <div className="wizard-preview" aria-label="Live plan preview">
          <span>Net worth {fmtCompactUsd(preview.now)}</span>
          <span>
            {preview.depletion ? `runs out ${preview.depletion}` : `ends with ${fmtCompactUsd(preview.ending)}`}
          </span>
          <span className="hint">updates as you answer</span>
        </div>
      )}

      <section className="card wizard-step">{body()}</section>

      {showSaveNudge && (
        <div className="wizard-nudge-backdrop" role="dialog" aria-modal="true" aria-label="Save your progress?">
          <div className="card wizard-nudge">
            <h3>Save your progress?</h3>
            <p className="hint">
              "Remember my progress on this device" is unchecked. Some of the coming steps
              take a while — if you close this tab or step away, your answers will be gone.
              Saving keeps them in this browser only; nothing is ever sent anywhere.
            </p>
            <div className="wizard-nav">
              <button className="btn" onClick={() => {
                setShowSaveNudge(false);
                goNext();
              }}>
                Continue without saving
              </button>
              <button className="btn primary" onClick={() => {
                setSaveHere(true);
                onRememberChange(true);
                setShowSaveNudge(false);
                goNext();
              }}>
                ✓ Remember &amp; continue
              </button>
            </div>
          </div>
        </div>
      )}

      {blocker && <p className="wizard-blocker" role="status">{blocker}</p>}

      <div className="wizard-nav">
        {!atStart && (
          <button className="btn" onClick={() => setStepIndex((i) => Math.max(0, i - 1))}>
            ← Back
          </button>
        )}
        {atReview ? (
          <button className="btn primary" onClick={() => {
            clearWizardDraft(); // setup is complete — future resumes open the plan
            onFinish(preview.plan);
          }}>
            Open my plan →
          </button>
        ) : (
          <button className="btn primary" disabled={blocker != null} onClick={() => {
            // Leaving step 1 without opting in: remind once that a break would
            // lose everything, right when it's cheapest to prevent.
            if (stepIndex === 0 && !saveHere && !nudged) {
              setNudged(true);
              setShowSaveNudge(true);
              return;
            }
            goNext();
          }}>
            Next →
          </button>
        )}
      </div>
    </div>
  );
}
